import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link, useNavigate } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserContext } from 'contexts/UserContext';
import { Project } from 'data/projects';
//import { Project, Status, projects } from 'data/projects';

// import Avatar from 'components/base/Avatar';
// import { ProgressBar } from 'react-bootstrap';
// import classNames from 'classnames';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionProjectDropdownItems from 'components/common/ActionProjectDropdownItems';
//import AvatarDropdown from 'components/common/AvatarDropdown';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useContext } from 'react';
import interceptor from 'services/interceptor';

const columns: ColumnDef<Project>[] = [
  {
    accessorKey: 'name',
    header: 'Project Name',
    cell: ({ row: { original } }) => {
      const { title } = original;
      return (
        <Link
          to={`/apps/project-management/project-details/${original.projectId}`}
          className="text-decoration-none fw-bold fs-8"
        >
          {title}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap' },
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    header: 'Start date',
    accessorKey: 'startDate',
    cell: ({ row: { original } }) => {
      const { startDate } = original;
      const startDateObj = startDate ? new Date(startDate) : null; // Parse the startDate into a Date object if it exists
      const formattedDate = startDateObj?.toLocaleDateString(); // Format the Date object to display only the date

      return (
        <Link
          to={`/apps/project-management/project-details/${original.projectId}`}
          className="text-decoration-none fw-bold fs-8"
        >
          {formattedDate}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    header: 'Deadline',
    accessorKey: 'deadline',
    cell: ({ row: { original } }) => {
      const { deadline } = original;
      const deadllineObj = deadline ? new Date(deadline) : null; // Parse the startDate into a Date object if it exists
      const formattedDate = deadllineObj?.toLocaleDateString(); // Format the Date object to display only the date

      return (
        <Link
          to={`/apps/project-management/project-details/${original.projectId}`}
          className="text-decoration-none fw-bold fs-8"
        >
          {formattedDate}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  },
  {
    id: 'budget',
    header: 'Budget',
    cell: ({ row: { original } }) => {
      const { budget } = original;
      if (budget) {
        return (
          <>
            <p className="fw-bold text-1100 fs-9 mb-0">
              {budget?.currency} {budget?.amount}
            </p>
            <p className="fw-semi-bold fs-10 text-700 mb-0">{budget?.status}</p>
          </>
        );
      }
      return (
        <Button variant="phoenix-secondary" className="btn-icon">
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      );
    },
    meta: {
      cellProps: { className: 'ps-3' },
      headerProps: { style: { width: '12%' }, className: 'ps-3' }
    }
  },
  {
    id: 'action',
    cell: ({ row: { original } }) => (
      <RevealDropdownTrigger>
        <RevealDropdown>
          <ActionProjectDropdownItems project={original} />
        </RevealDropdown>
      </RevealDropdownTrigger>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

const ProjectDashboardTable = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const navigate = useNavigate();
  const context = useContext(UserContext);

  useEffect(() => {
    if (!context || !context.user) {
      navigate('/pages/authentication/split/sign-in');
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    localStorage.setItem('np_pitchDeckSearchResults', JSON.stringify([]));
    interceptor.apiWithAuth
      .get(`/project/get-all-by-user/${userId}`)
      .then(response => {
        setProjects(response.data);

        // save search results to local storage for quick searching
        const searchProjectItems = response.data.map(
          (project: {
            projectId: string;
            image: string;
            title: string;
            briefOutline: string;
          }) => ({
            url:
              `/apps/project-management/project-details/` + project.projectId,
            image: project.image,
            label: project.title,
            details: project.briefOutline,
            category: 'projects',
            projectId: project.projectId // Include projectId here
          })
        );

        localStorage.setItem(
          'np_projectSearchResults',
          JSON.stringify(searchProjectItems)
        );

        // iterate over searchProjectItems, get the pitchdecks for each project and save them to local storage
        searchProjectItems.forEach(
          (p: {
            projectId: string;
            image: string;
            label: string;
            details: string;
            category: string;
          }) => {
            interceptor.apiWithAuth
              .get(`/pitchdeck/get-pitchdecks-by-project-id/${p.projectId}`)
              .then(response => {
                const searchPitchDeckItems = response.data.map(
                  (pitchDeck: {
                    pitchDeckId: string;
                    projectId: string;
                    title: string;
                    Thumbnail: string;
                  }) => ({
                    url:
                      `/pages/slide-editor/` +
                      pitchDeck.projectId +
                      '/' +
                      pitchDeck.pitchDeckId,
                    thumbnail: pitchDeck.Thumbnail,
                    label: pitchDeck.title,
                    category: 'pitch_decks'
                  })
                );

                const existingPitchDeckItems = JSON.parse(
                  localStorage.getItem('np_pitchDeckSearchResults') || '[]'
                );
                const updatedPitchDeckItems =
                  existingPitchDeckItems.concat(searchPitchDeckItems);

                localStorage.setItem(
                  'np_pitchDeckSearchResults',
                  JSON.stringify(updatedPitchDeckItems)
                );
              })
              .catch(error => {
                console.error(error);
              });
          }
        );
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  const table = useAdvanceTable({
    data: projects,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
        />
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>
    </div>
  );
};

export default ProjectDashboardTable;
